import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

import { LoadingButton } from '../../common/LoadingButton';
import { useInputFormat, useInputFormats } from '../../../services/llm-input-format.service';
import { useLLMModelInputDryRun } from '../../../services/llm-model-input.service';
import { InputFieldDef, ModelFieldOverrides } from '../../../types/admin';
import { DryRunResult } from '../input-format/DryRunResult';
import { formatValue } from '../input-format/inputFieldDialogHelpers';
import { FieldOverrideDialog } from '../input-format/FieldOverrideDialog';
import { ImportSchemaDialog } from '../input-format/ImportSchemaDialog';
import { InputFieldListEditor } from '../input-format/InputFieldListEditor';
import { mergeImportedFields } from '../input-format/mergeImportedFields';
import { orderFields, rewriteOrder } from '../input-format/inputFieldOrder';

export interface ModelInputTabProps {
  /** Saved model id; dry-run needs it. Undefined while creating. */
  modelPublicId?: string;
  providerPublicId: string;
  inputFormatPublicId?: string | null;
  fieldOverrides?: ModelFieldOverrides | null;
  onFormatChange: (publicId: string | null) => void;
  onOverridesChange: (value: ModelFieldOverrides | null) => void;
  /** True when format/overrides differ from what is saved (dry-run uses saved data only). */
  isDirty: boolean;
}

const keyOf = (field: InputFieldDef) => field.key || field.name;
/** Override map key; direction-prefixed so an input `image` never patches an output `image`. */
const overrideKeyOf = (field: InputFieldDef) =>
  `${field.direction === 'out' ? 'out' : 'in'}:${keyOf(field)}`;
const isInput = (field: InputFieldDef) => field.direction !== 'out';

export const ModelInputTab: React.FC<ModelInputTabProps> = ({
  modelPublicId,
  providerPublicId,
  inputFormatPublicId,
  fieldOverrides,
  onFormatChange,
  onOverridesChange,
  isDirty,
}) => {
  const { data: formats = [] } = useInputFormats();
  const { data: format, isLoading: isLoadingFormat } = useInputFormat(
    inputFormatPublicId ?? undefined
  );
  const dryRun = useLLMModelInputDryRun();
  const [importOpen, setImportOpen] = React.useState(false);
  const [overrideField, setOverrideField] = React.useState<InputFieldDef | null>(null);
  const [dryRunText, setDryRunText] = React.useState('{}');
  const [dryRunError, setDryRunError] = React.useState('');

  const selectable = formats.filter(
    (item) =>
      item.isActive &&
      (!item.providerPublicId ||
        item.providerPublicId === providerPublicId ||
        item.publicId === inputFormatPublicId)
  );
  const modelFields = orderFields(fieldOverrides?.fields ?? []);
  const modelKeys = new Set(modelFields.map(keyOf));
  const overrides = fieldOverrides?.overrides ?? {};
  const inherited = orderFields((format?.fields ?? []).filter(isInput)).filter(
    (field) => !modelKeys.has(keyOf(field))
  );
  const outCount = (format?.fields ?? []).filter((field) => !isInput(field)).length;

  const write = (next: ModelFieldOverrides) => {
    const nextOverrides =
      next.overrides && Object.keys(next.overrides).length > 0 ? next.overrides : undefined;
    const nextFields = next.fields && next.fields.length > 0 ? next.fields : undefined;
    onOverridesChange(
      nextOverrides || nextFields
        ? {
            ...(nextOverrides ? { overrides: nextOverrides } : {}),
            ...(nextFields ? { fields: nextFields } : {}),
          }
        : null
    );
  };

  const saveOverride = (field: InputFieldDef, patch: Partial<InputFieldDef> | null) => {
    const next = { ...overrides };
    // Drop the legacy un-prefixed entry so the saved patch is the only one left.
    delete next[keyOf(field)];
    if (patch && Object.keys(patch).length > 0) next[overrideKeyOf(field)] = patch;
    else delete next[overrideKeyOf(field)];
    write({ ...fieldOverrides, overrides: next });
    setOverrideField(null);
  };

  const runDryRun = async () => {
    if (!modelPublicId) return;
    setDryRunError('');
    try {
      const parsed = JSON.parse(dryRunText || '{}');
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setDryRunError('Values must be a JSON object');
        return;
      }
      await dryRun.mutateAsync({ publicId: modelPublicId, values: parsed });
    } catch {
      setDryRunError('Values must be valid JSON');
    }
  };

  return (
    <Box className="space-y-4">
      <Alert severity="info">
        Only models that take structured input (video, image, ...) need a format. Chat, text and
        embedding models can leave it empty.
      </Alert>

      <Box className="flex flex-wrap items-start gap-3">
        <FormControl size="small" className="min-w-[280px] flex-1">
          <InputLabel>Input format</InputLabel>
          <Select
            label="Input format"
            value={inputFormatPublicId ?? ''}
            onChange={(event) => onFormatChange(event.target.value || null)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {selectable.map((item) => (
              <MenuItem key={item.publicId} value={item.publicId}>
                {item.name} (v{item.version})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" className="pt-2 text-gray-500 dark:text-slate-400">
          Manage formats in{' '}
          <Link className="text-blue-600 dark:text-blue-400" to="/input-formats">
            Input Formats
          </Link>
        </Typography>
      </Box>

      {inputFormatPublicId && (
        <Paper variant="outlined" className="p-3 dark:bg-slate-800">
          <Box className="mb-2 flex flex-wrap items-center gap-2">
            <Typography variant="subtitle2">Fields from the format</Typography>
            {format && <Chip size="small" label={`v${format.version}`} />}
            {outCount > 0 && (
              <Chip size="small" variant="outlined" label={`${outCount} response`} />
            )}
          </Box>
          {isLoadingFormat ? (
            <Box className="flex justify-center p-4">
              <CircularProgress size={24} />
            </Box>
          ) : inherited.length === 0 ? (
            <Alert severity="info">The format has no input fields.</Alert>
          ) : (
            <Box className="overflow-x-auto rounded border border-slate-200 dark:border-slate-700">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Default</TableCell>
                    <TableCell>Enum</TableCell>
                    <TableCell align="right">Override</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inherited.map((field) => {
                    const patch = overrides[overrideKeyOf(field)] ?? overrides[keyOf(field)];
                    return (
                      <TableRow key={keyOf(field)} className={patch?.hidden ? 'opacity-50' : ''}>
                        <TableCell className="font-mono">
                          {field.name}
                          {patch?.hidden && <Chip size="small" className="ml-2" label="hidden" />}
                        </TableCell>
                        <TableCell>{field.type}</TableCell>
                        <TableCell>{(patch?.required ?? field.required) ? 'Yes' : 'No'}</TableCell>
                        <TableCell className="font-mono">
                          {formatValue(patch?.default ?? field.default)}
                        </TableCell>
                        <TableCell>{(patch?.enum ?? field.enum)?.join(', ') || '-'}</TableCell>
                        <TableCell align="right">
                          <Button size="small" onClick={() => setOverrideField(field)}>
                            {patch ? 'Edit override' : 'Override'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      )}

      <Box>
        <Box className="flex items-center justify-between">
          <Typography variant="subtitle2">Model fields</Typography>
          <Button size="small" onClick={() => setImportOpen(true)}>
            Import JSON schema
          </Button>
        </Box>
        <Typography variant="caption" className="mb-2 block text-gray-500 dark:text-slate-400">
          Fields only this model has. A model field with the same key replaces the one from the
          format.
        </Typography>
        <InputFieldListEditor
          direction="in"
          fields={modelFields}
          onChange={(next) => write({ ...fieldOverrides, fields: rewriteOrder(next) })}
        />
      </Box>

      <Divider />
      <Box className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TextField
          label="Dry-run values JSON"
          value={dryRunText}
          onChange={(event) => setDryRunText(event.target.value)}
          error={!!dryRunError}
          helperText={
            dryRunError ||
            (!modelPublicId
              ? 'Save the model first'
              : isDirty
                ? 'Save before running a dry-run (saved data only)'
                : 'Builds the request body from the saved format')
          }
          multiline
          rows={6}
          inputProps={{ className: 'font-mono text-xs' }}
        />
        <Box>
          <LoadingButton
            variant="outlined"
            loading={dryRun.isPending}
            disabled={!modelPublicId || isDirty}
            onClick={runDryRun}
            className="mb-3"
          >
            Dry run
          </LoadingButton>
          {dryRun.data && <DryRunResult result={dryRun.data} />}
        </Box>
      </Box>

      <ImportSchemaDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={(imported) =>
          write({
            ...fieldOverrides,
            fields: rewriteOrder(mergeImportedFields(modelFields, imported.filter(isInput))),
          })
        }
      />

      {overrideField && (
        <FieldOverrideDialog
          field={overrideField}
          value={overrides[overrideKeyOf(overrideField)] ?? overrides[keyOf(overrideField)]}
          onClose={() => setOverrideField(null)}
          onSave={(patch) => saveOverride(overrideField, patch)}
        />
      )}
    </Box>
  );
};
